import { useState } from "react";
import {
    ChevronDownIcon,
    ChevronRightIcon,
    MailIcon,
    PlayCircleIcon,
    CheckCircle2Icon,
    XCircleIcon,
    RefreshCwIcon,
    SparklesIcon,
} from "lucide-react";
import type { ActivityEvent } from "@service/training";
import { cn } from "@util/tailwind";

const TYPE_META: Record<
    ActivityEvent["type"],
    { icon: React.ComponentType<{ className?: string }>; cls: string }
> = {
    campaign_created: { icon: SparklesIcon,     cls: "text-blue-600 bg-blue-50"     },
    invite_sent:      { icon: MailIcon,         cls: "text-gray-600 bg-gray-50"     },
    session_started:  { icon: PlayCircleIcon,   cls: "text-blue-600 bg-blue-50"     },
    session_completed:{ icon: CheckCircle2Icon, cls: "text-emerald-600 bg-emerald-50" },
    session_abandoned:{ icon: XCircleIcon,      cls: "text-amber-600 bg-amber-50"   },
    retry_started:    { icon: RefreshCwIcon,    cls: "text-blue-600 bg-blue-50"     },
};

function getEventLabel(e: ActivityEvent): string {
    switch (e.type) {
        case "campaign_created":  return "Campaign created";
        case "invite_sent":       return e.employee_name ? `Invite sent to ${e.employee_name}` : (e.detail ?? "Invites sent");
        case "session_started":   return "Session started";
        case "session_completed": return "Session completed";
        case "session_abandoned": return "Session abandoned";
        case "retry_started":     return "Retry started";
    }
}


function formatEventTime(iso: string): string {
    const d = new Date(iso);
    const day   = d.getDate();
    const month = d.toLocaleString("en-US", { month: "short" });
    const year  = d.getFullYear();
    const time  = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    return `${day} ${month} ${year} at ${time}`;
}

function getDateKey(iso: string): string {
    return new Date(iso).toLocaleDateString("en-CA"); // "2026-05-13"
}

function getGroupLabel(key: string): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const d = new Date(key + "T00:00:00");
    if (d >= today) return "Today";
    if (d >= yesterday) return "Yesterday";
    return d.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
}

interface DateGroup { key: string; label: string; events: ActivityEvent[] }

function buildGroups(events: ActivityEvent[]): DateGroup[] {
    const map = new Map<string, ActivityEvent[]>();
    for (const e of events) {
        const k = getDateKey(e.ts);
        if (!map.has(k)) map.set(k, []);
        map.get(k)!.push(e);
    }
    const keys = Array.from(map.keys()).sort((a, b) => b.localeCompare(a));
    return keys.map((k) => ({ key: k, label: getGroupLabel(k), events: map.get(k)! }));
}

interface Props {
    events: ActivityEvent[];
    collapsible?: boolean;
}

export function ActivityLog({ events, collapsible = true }: Props) {
    const [outerOpen, setOuterOpen] = useState(!collapsible);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

    const groups = buildGroups(events);

    const toggleGroup = (key: string) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const body = (
        <div>
            {groups.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No activity yet.</p>
            ) : (
                <div className="space-y-4">
                    {groups.map((group) => {
                        const isCollapsed = collapsed.has(group.key);
                        return (
                            <div key={group.key}>
                                <button
                                    onClick={() => toggleGroup(group.key)}
                                    className="flex items-center gap-1.5 mb-2 w-full text-left"
                                >
                                    {isCollapsed
                                        ? <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                        : <ChevronDownIcon  className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    }
                                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                        {group.label}
                                    </span>
                                    <span className="text-xs text-gray-400">({group.events.length})</span>
                                </button>

                                {!isCollapsed && (
                                    <ol className="space-y-3 pl-5">
                                        {group.events.map((e) => {
                                            const meta = TYPE_META[e.type];
                                            const Icon = meta.icon;
                                            return (
                                                <li key={e.id} className="flex items-start gap-3">
                                                    <div className={cn(
                                                        "flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5",
                                                        meta.cls
                                                    )}>
                                                        <Icon className="w-3.5 h-3.5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {getEventLabel(e)}
                                                        </p>
                                                        {e.detail && !(e.type === "invite_sent" && !e.employee_name) && (
                                                            <p className="text-xs text-gray-500">{e.detail}</p>
                                                        )}
                                                        {e.performed_by && (
                                                            <p className="text-xs text-gray-400 mt-0.5">
                                                                Performed by: <span className="font-medium text-gray-600">{e.performed_by}</span>
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex-shrink-0 text-right">
                                                        <p className="text-xs text-gray-400 whitespace-nowrap">
                                                            {formatEventTime(e.ts)}
                                                        </p>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ol>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    if (!collapsible) {
        return body;
    }

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-5">
            <button
                onClick={() => setOuterOpen((o) => !o)}
                className="w-full flex items-center justify-between mb-0"
            >
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800">Activity log</span>
                    <span className="text-xs text-gray-400">({events.length})</span>
                </div>
                <span className="text-xs text-gray-400">{outerOpen ? "Hide" : "Show"}</span>
            </button>
            {outerOpen && <div className="mt-4">{body}</div>}
        </div>
    );
}
